import {useEffect, useState} from 'react';

import {searchAccountProductSubscriptions} from 'src/openapi/admin/apis/AccountProductSubscriptionsApi';
import {useAppDispatch} from 'src/store/hooks';

import type {Error} from 'src/openapi/admin/models/Error';
import type {ProductSubscription} from 'src/openapi/admin/models/ProductSubscription';

const PAGE_SIZE = 100;

export const useAccountProductSeatUnassignmentTerms = (accountId: number) => {
    const apiDispatch = useAppDispatch();
    const [terms, setTerms] = useState<ProductSubscription[] | null>(null);
    const [error, setError] = useState<Error | null>(null);

    useEffect(() => {
        const abortController = new AbortController();
        setTerms(null);
        setError(null);

        const loadTerms = async () => {
            try {
                const loadedTerms: ProductSubscription[] = [];
                let page = 1;
                let totalCount = 0;

                do {
                    const response = await apiDispatch(searchAccountProductSubscriptions({
                        accountId,
                        productSubscriptionSearchRequest: {pagination: {page, pageSize: PAGE_SIZE}},
                    }, {signal: abortController.signal}));

                    if (abortController.signal.aborted) {
                        return;
                    }
                    if (!response.payload?.success) {
                        throw response.payload?.data;
                    }

                    const result = response.payload.data;
                    const pageTerms = result?.content || [];
                    loadedTerms.push(...pageTerms);
                    totalCount = result?.totalCount || loadedTerms.length;
                    if (!pageTerms.length && loadedTerms.length < totalCount) {
                        throw new Error('Unable to load all Product Access Terms. Please try again.');
                    }
                    page += 1;
                } while (loadedTerms.length < totalCount);

                if (!abortController.signal.aborted) {
                    setTerms(loadedTerms.filter(term => term.activeSeatCount > 0));
                }
            } catch (requestError) {
                if (!abortController.signal.aborted) {
                    setTerms([]);
                    setError(requestError as Error);
                }
            }
        };

        loadTerms();
        return () => abortController.abort();
    }, [accountId, apiDispatch]);

    return {terms, error};
};
